import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FaTimes,
  FaExclamationTriangle,
  FaLink,
  FaServer,
  FaDatabase,
  FaInfoCircle,
  FaSearch
} from 'react-icons/fa';
import * as Sentry from "@sentry/react";

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'volume-attach-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'volume-attach-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'volume-attach-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please contact support.',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'VM_NOT_FOUND': 'Virtual machine not found or you do not have permission to access it',
  'VM_NOT_ACTIVE': 'VM must be in ACTIVE state to attach volumes',
  'VM_NOT_SHUTOFF': 'VM must be shutoff to attach volumes',
  'NO_ACTIVE_VMS': 'No active VMs available for attachment',
  'NO_SHUTOFF_VMS': 'No shutoff VMs available for attachment',
  'NO_VMS_AVAILABLE': 'No virtual machines found in this region',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please try again.',
  'VOLUME_IN_USE': 'Volume is currently in use and cannot be attached. Please wait for it to become available.',
  'VOLUME_ATTACH_FAILED': 'Unable to attach volume. Please try again.',
  'VOLUME_NOT_FOUND': 'Volume not found or you do not have permission to access it',
  'VOLUME_NOT_DETACHED': 'Volume must be detached before attaching',
  'VOLUME_NOT_AVAILABLE': 'Volume must be available before attaching',
  'REGION_MISMATCH': 'Volume and VM must be in the same region for attachment',
  'INVALID_VOLUME_PARAMETERS': 'Invalid volume configuration. Please check your inputs.',
  'VALIDATION_ERROR': 'Invalid request parameters. Please check your selection and try again.',
  'INVALID_JSON': 'Invalid request format. Please try again.',
  'INVALID_DATA': 'Invalid input data',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again.',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'DB_ERROR': 'Service temporarily unavailable',
  'NETWORK_ERROR': 'Unable to connect. Please check your internet connection',
  // Network Errors (6-position detection)
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
  'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  if (error.response?.status >= 500 || error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  if (error.response?.status === 429 || error.status === 429) {
    const retryAfter = error.response?.headers['retry-after'] || 60;
    return `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
  }
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  if (error.response?.status === 402 || error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  if (error.userMessage) {
    return error.userMessage;
  }
  if (error.error) {
    return error.error;
  }
  
  return 'Something went wrong. Please try again.';
};

// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline (backup for 2.1)
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ============================================================================
// REGION & VM HELPERS
// ============================================================================
const getRegionCapabilities = (region) => {
  const advancedRegions = ['FINLAND-1', 'FINLAND-2', 'FINLAND-3'];
  const legacyRegions = ['CANADA-1'];
  
  return {
    supportsAdvancedOperations: advancedRegions.includes(region),
    supportsOSImages: legacyRegions.includes(region),
    isAdvancedRegion: advancedRegions.includes(region),
    isLegacyRegion: legacyRegions.includes(region)
  };
};

const getVMStatus = (vm, volumeRegion) => {
  if (!vm || !vm.status) return null;
  
  const capabilities = getRegionCapabilities(volumeRegion);
  
  if (capabilities.isAdvancedRegion) {
    const status = vm.status.toLowerCase();
    if (status === 'running') return 'ACTIVE';
    if (status === 'offline' || status === 'stopped') return 'SHUTOFF';
    if (status === 'provisioning') return 'CREATING';
    if (status === 'starting') return 'STARTING';
    if (status === 'stopping') return 'STOPPING';
    if (status === 'shelved_offloaded') return 'HIBERNATED';
    return vm.status.toUpperCase();
  } else {
    const status = vm.status.toLowerCase();
    if (status === 'active') return 'ACTIVE';
    if (status === 'stopped' || status === 'shutoff') return 'SHUTOFF';
    if (status === 'suspended') return 'SUSPENDED';
    if (status === 'paused') return 'PAUSED';
    return vm.status.toUpperCase();
  }
};

const getVMRegion = (vm) => {
  const region = vm.region || vm.availability_zone || vm.zone || null;
  
  const locationToRegion = {
    'FIN-01': 'FINLAND-1',
    'FIN-02': 'FINLAND-2', 
    'FIN-03': 'FINLAND-3',
    'Finland 1': 'FINLAND-1',
    'Finland 2': 'FINLAND-2',
    'Finland 3': 'FINLAND-3',
    'Canada 1': 'CANADA-1'
  };
  
  return locationToRegion[region] || region;
};

const getVMId = (vm) => {
  return vm.instance_id || vm.server_uuid || vm.id;
};

const getVMName = (vm) => {
  return vm.name || vm.hostname || `VM-${getVMId(vm)}` || 'Unknown VM';
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting = false) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '28px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '220px',
    padding: '28px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '24px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
subtitle: {
    color: '#93c5fd',
    margin: 0,
    fontSize: '13px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '8px',
    display: 'block',
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: '16px',
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6b7280',
  },
  input: {
    width: '100%',
    padding: '12px 14px 12px 44px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  vmList: {
    maxHeight: '220px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  vmCard: (selected, disabled) => ({
    padding: '14px',
    backgroundColor: selected ? '#1c2b22' : '#1a1a1a',
    border: `1px solid ${selected ? '#e0eacc' : '#333'}`,
    borderRadius: '10px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  }),
  vmName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    margin: 0,
  },
  vmRegion: {
    color: '#6b7280',
    fontSize: '11px',
    margin: '2px 0 0 0',
  },
  vmDisabledText: {
    color: '#ff8080',
    fontSize: '11px',
    margin: '2px 0 0 0',
  },
  statusBadge: (status) => ({
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: status === 'SHUTOFF' ? 'rgba(202,217,238,0.1)' : 'rgba(202,217,238,0.15)',
    color: '#cad9ee',
    border: status === 'SHUTOFF' ? '1px solid rgba(202,217,238,0.3)' : '1px solid #cad9ee',
  }),
  radio: (selected) => ({
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: `2px solid ${selected ? '#e0eacc' : '#555'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  radioInner: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#e0eacc',
  },
summaryHeader: {
    color: '#6b7280',
    fontSize: '11px',
    letterSpacing: '1.5px',
    margin: '0 0 12px 0',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '11px',
    margin: '0 0 4px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryValue: {
    color: 'white',
    fontSize: '14px',
    margin: '0 0 16px 0',
    wordBreak: 'break-all',
  },
  submitBtn: (disabled) => ({
    width: '100%',
    padding: '14px 24px',
    backgroundColor: disabled ? '#3d4435' : '#e0eacc',
    color: disabled ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.15s',
  }),
  cancelBtn: (disabled) => ({
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: disabled ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  }),
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  spinnerLight: {
    width: '32px',
    height: '32px',
    border: '3px solid #333',
    borderTopColor: '#e0eacc',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    marginBottom: '16px',
  },
  emptyTitle: {
    color: 'white',
    fontSize: '15px',
    fontWeight: '500',
    margin: '0 0 8px 0',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '13px',
    margin: 0,
    lineHeight: '1.5',
  },
  actionHint: {
    marginTop: '16px',
    padding: '10px 16px',
    backgroundColor: 'rgba(224,234,204,0.08)',
    border: '1px solid rgba(224,234,204,0.2)',
    borderRadius: '8px',
  },
  actionHintText: {
    color: '#e0eacc',
    fontSize: '12px',
    margin: 0,
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#fca5a5',
    fontSize: '13px',
  },
  infoBox: {
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoText: {
    color: '#93c5fd',
    fontSize: '12px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  loadingText: {
    color: '#9ca3af',
    fontSize: '14px',
    margin: 0,
  },
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const VolumeAttachModal = ({ isOpen, onClose, onAttach, onRefresh, volume, vms, isAttaching, isLoadingVms }) => {
  const [selectedVmId, setSelectedVmId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  
  const [loadingStates, setLoadingStates] = useState({
    validating: false,
    submitting: false
  });
  
  const abortControllerRef = useRef(null);
  const maxRetries = 3;
  const retryAttempts = useRef(0);

  const isSubmitting = loadingStates.submitting || isAttaching;
  const styles = getStyles(isSubmitting);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending volume attach requests');
      }
    };
  }, []);

  // Debounced refresh
  const debouncedRefresh = useCallback(
    debounce(() => {
      if (onRefresh && typeof onRefresh === 'function') {
        logInfo('Refreshing VM data for attach modal', {
          volumeId: volume?.id,
          volumeRegion: volume?.region
        });
        onRefresh();
      }
    }, 300),
    [onRefresh, volume?.id, volume?.region]
  );

  // Focus/Visibility refresh
  useEffect(() => {
    const handleFocus = () => {
      if (isOpen) {
        logInfo('Refreshing VM data on page focus for attach modal');
        debouncedRefresh();
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden && isOpen) {
        logInfo('Refreshing VM data on visibility change for attach modal');
        debouncedRefresh();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, debouncedRefresh]);

  // Memoized same-region VMs
  const sameRegionVms = useMemo(() => {
    if (!Array.isArray(vms) || !volume?.region) return [];
    
    const filtered = vms.filter(vm => {
      if (!vm) return false;
      const vmRegion = getVMRegion(vm);
      return vmRegion === volume.region;
    });
    
    logInfo('Same region VMs calculated', {
      totalVms: vms.length,
      sameRegionVms: filtered.length,
      volumeId: volume?.id,
      volumeRegion: volume?.region
    });
    
    return filtered;
  }, [vms, volume?.region, volume?.id]);

  // Memoized compatible VMs (SHUTOFF only)
  const compatibleVms = useMemo(() => {
    if (!Array.isArray(sameRegionVms)) return [];
    
    const capabilities = getRegionCapabilities(volume?.region);
    
    const compatible = sameRegionVms.filter(vm => {
      if (!vm) return false;
      const mappedStatus = getVMStatus(vm, volume?.region);
      return mappedStatus === 'SHUTOFF';
    });
    
    logInfo('Compatible VMs calculated', {
      sameRegionVms: sameRegionVms.length,
      compatibleVms: compatible.length,
      volumeId: volume?.id,
      volumeRegion: volume?.region,
      isAdvancedRegion: capabilities.isAdvancedRegion,
      requiredStatus: 'SHUTOFF'
    });
    
    return compatible;
  }, [sameRegionVms, volume?.region, volume?.id]);

// Memoized filtered VMs based on search
  const filteredVms = useMemo(() => {
    if (!searchTerm.trim()) return sameRegionVms;
    return sameRegionVms.filter(vm => 
      getVMName(vm).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sameRegionVms, searchTerm]);

  // Memoized validation results
  const validationResults = useMemo(() => {
    const capabilities = getRegionCapabilities(volume?.region);
    
    const results = {
      hasVms: Array.isArray(vms) && vms.length > 0,
      hasSameRegionVms: sameRegionVms.length > 0,
      hasCompatibleVms: compatibleVms.length > 0,
      validVolume: volume && volume.id && volume.region,
      isAdvancedRegion: capabilities.isAdvancedRegion,
      requiredStatus: 'SHUTOFF',
      isLoadingVms: isLoadingVms
    };
    
    logInfo('Validation results calculated', {
      ...results,
      volumeId: volume?.id,
      volumeRegion: volume?.region,
      totalVms: vms?.length || 0,
      sameRegionVmCount: sameRegionVms.length,
      compatibleVmCount: compatibleVms.length
    });
    
    return results;
  }, [vms, sameRegionVms, compatibleVms, volume, isLoadingVms]);

  // Set Sentry context
  useEffect(() => {
    if (isOpen) {
      const capabilities = getRegionCapabilities(volume?.region);
      
      Sentry.setContext("volume-attach-modal", {
        volumeId: volume?.id,
        volumeName: volume?.name,
        volumeRegion: volume?.region,
        totalVms: vms?.length || 0,
        sameRegionVms: sameRegionVms.length,
        compatibleVms: compatibleVms.length,
        isAdvancedRegion: capabilities.isAdvancedRegion,
        requiredStatus: 'SHUTOFF',
        isOpen: true
      });
      
      logInfo('Volume attach modal opened', {
        volumeId: volume?.id,
        volumeName: volume?.name,
        volumeRegion: volume?.region,
        totalVms: vms?.length || 0,
        sameRegionVms: sameRegionVms.length,
        compatibleVms: compatibleVms.length
      });
    }
  }, [isOpen, volume?.id, volume?.name, volume?.region, vms?.length, sameRegionVms.length, compatibleVms.length]);

// Status style helper
  const getStatusStyle = useCallback((status) => {
    if (status === 'SHUTOFF') {
      return { background: '#1c2b22', color: '#a5c77f', border: '1px solid #a5c77f' };
    }
    return { background: 'rgba(107,114,128,0.15)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.5)' };
  }, []);
  // VM attachment capability check
  const canAttachToVm = useCallback((vm) => {
    if (!vm) return false;
    const mappedStatus = getVMStatus(vm, volume?.region);
    return mappedStatus === 'SHUTOFF';
  }, [volume?.region]);

  // Reset state on modal open
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSearchTerm('');
      setLoadingStates({ validating: false, submitting: false });
      
      logInfo('Modal state reset on open', {
        volumeId: volume?.id,
        hasVms: validationResults.hasVms,
        hasSameRegionVms: validationResults.hasSameRegionVms,
        hasCompatibleVms: validationResults.hasCompatibleVms
      });
      
      if (validationResults.hasCompatibleVms) {
        const firstCompatibleVm = compatibleVms[0];
        if (firstCompatibleVm) {
          const vmId = getVMId(firstCompatibleVm);
          setSelectedVmId(vmId);
          
          logInfo('Auto-selected first compatible VM', {
            volumeId: volume?.id,
            selectedVmId: vmId,
            selectedVmName: getVMName(firstCompatibleVm)
          });
        }
      } else {
        setSelectedVmId('');
      }
    }
  }, [isOpen, validationResults, compatibleVms, volume?.id]);

  // VM selection handler
  const handleVmSelection = useCallback((vmId) => {
    const vm = compatibleVms.find(vm => getVMId(vm) === vmId);
    
    logUserAction('vm_selection_attempted', {
      volumeId: volume?.id,
      selectedVmId: vmId,
      selectedVmName: getVMName(vm),
      canAttach: canAttachToVm(vm)
    });
    
    if (vm && canAttachToVm(vm)) {
      setSelectedVmId(vmId);
      setError(null);
      
      logInfo('VM selected successfully', {
        volumeId: volume?.id,
        selectedVmId: vmId,
        selectedVmName: getVMName(vm)
      });
    }
  }, [compatibleVms, canAttachToVm, volume?.id]);

  // Form submission handler
const handleSubmit = useCallback(async (e) => {
  e.preventDefault();
  
  const startTime = performance.now();
  
  logUserAction('volume_attach_submit_initiated', {
    volumeId: volume?.id,
    selectedVmId,
    volumeName: volume?.name,
    volumeRegion: volume?.region
  });
  
  setLoadingStates(prev => ({ ...prev, submitting: true }));
  setError(null);
  
  if (!selectedVmId) {
    const errorMessage = 'Please select a virtual machine';
    setError(errorMessage);
    setLoadingStates(prev => ({ ...prev, submitting: false }));
    logError('Form validation failed: No VM selected', { volumeId: volume?.id });
    return;
  }
  
  const selectedVm = compatibleVms.find(vm => getVMId(vm) === selectedVmId);
  
  if (!selectedVm) {
    const errorMessage = 'Selected virtual machine not found';
    setError(errorMessage);
    setLoadingStates(prev => ({ ...prev, submitting: false }));
    logError('Form validation failed: Selected VM not found', { volumeId: volume?.id, selectedVmId });
    return;
  }
  
  const currentVmStatus = getVMStatus(selectedVm, volume?.region);
  
  if (currentVmStatus !== 'SHUTOFF') {
    const errorMessage = `The VM must be shutoff to attach volumes. Current status: ${sanitizeText(currentVmStatus)}`;
    setError(errorMessage);
    setLoadingStates(prev => ({ ...prev, submitting: false }));
    logError('Form validation failed: VM not in SHUTOFF state', {
      volumeId: volume?.id,
      selectedVmId,
      vmStatus: currentVmStatus
    });
    return;
  }
  
  if (!volume || !volume.id) {
    const errorMessage = 'Invalid volume data';
    setError(errorMessage);
    setLoadingStates(prev => ({ ...prev, submitting: false }));
    logError('Form validation failed: Invalid volume data', { volumeId: volume?.id });
    return;
  }
  
  const vmRegion = getVMRegion(selectedVm);
  if (vmRegion !== volume.region) {
    const errorMessage = `Volume region (${volume.region}) must match VM region (${vmRegion})`;
    setError(errorMessage);
    setLoadingStates(prev => ({ ...prev, submitting: false }));
    logError('Form validation failed: Region mismatch', {
      volumeId: volume.id,
      volumeRegion: volume.region,
      vmRegion
    });
    return;
  }
  
  try {
    const response = await onAttach(selectedVmId, volume.id);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response === false || (response && !response.success)) {
      setLoadingStates(prev => ({ ...prev, submitting: false }));
      logError('Volume attachment failed from backend', {
        volumeId: volume.id,
        selectedVmId,
        duration: `${duration.toFixed(2)}ms`
      });
      return;
    }
    
    onClose();
    
    logPerformance('volume_attach_submit', duration, {
      volumeId: volume.id,
      selectedVmId,
      success: true
    });
    
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.response?.status === 401 || err.status === 401) {
      const errorMessage = ERROR_MESSAGES['AUTH_ERROR'];
      setError(errorMessage);
      setLoadingStates(prev => ({ ...prev, submitting: false }));
      logError('Volume attachment error - authentication failed', { volumeId: volume.id }, err);
      return;
    }
    
    // Position 2: Network error (no response)
    if (!err.response) {
      const errorMessage = err.error || getNetworkErrorMessage(err).message;
      const errorCode = err.code || getNetworkErrorMessage(err).code;
      setError(errorMessage);
      setLoadingStates(prev => ({ ...prev, submitting: false }));
      logError('Network error in handleSubmit', { 
        code: errorCode, 
        originalCode: err.code,
        online: navigator.onLine,
        volumeId: volume.id,
        selectedVmId,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Rate limit
    if (err.response?.status === 429) {
      const retryAfter = err.response?.headers?.['retry-after'] || 60;
      setError(`Too many requests. Please wait ${retryAfter} seconds.`);
      setLoadingStates(prev => ({ ...prev, submitting: false }));
      return;
    }
    
    // Position 4: Backend error code
    if (err.response?.data?.code && ERROR_MESSAGES[err.response.data.code]) {
      setError(ERROR_MESSAGES[err.response.data.code]);
      setLoadingStates(prev => ({ ...prev, submitting: false }));
      logError('Volume attachment error - backend code', { 
        code: err.response.data.code,
        volumeId: volume.id,
        selectedVmId,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 5: Direct error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      setLoadingStates(prev => ({ ...prev, submitting: false }));
      logError('Volume attachment error - error code', { 
        code: err.code,
        volumeId: volume.id,
        selectedVmId,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 6: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);
    setLoadingStates(prev => ({ ...prev, submitting: false }));
    
    logError('Volume attachment failed with exception', {
      volumeId: volume.id,
      selectedVmId,
      duration: `${duration.toFixed(2)}ms`
    }, err);
  }
}, [selectedVmId, compatibleVms, volume, onAttach, onClose]);

// Close handler
  const handleClose = useCallback(() => {
    logUserAction('close_volume_attach_modal', {
      volumeId: volume?.id,
      selectedVmId,
      wasVmSelected: !!selectedVmId
    });
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setSelectedVmId('');
    setSearchTerm('');
    setError(null);
    setLoadingStates({ validating: false, submitting: false });
    
    onClose();
  }, [onClose, volume?.id, selectedVmId]);
  
  if (!isOpen) return null;

  // Get volume display values
  const volumeName = volume?.display_name || volume?.name || '';
  const volumeSize = volume?.size || 0;
  const volumeType = volume?.volume_type || volume?.type || 'Standard';
  const volumeRegion = volume?.region || '';

const getButtonState = () => {
    if (isLoadingVms) {
      return { disabled: true, text: 'Loading...', showSpinner: true };
    }
    if (isSubmitting) {
      return { disabled: true, text: 'Attaching...', showSpinner: true };
    }
    if (!validationResults.hasVms) {
      return { disabled: true, text: 'No VMs Available', showSpinner: false };
    }
    if (!validationResults.hasSameRegionVms) {
      return { disabled: true, text: 'No VMs Available', showSpinner: false };
    }
    if (!selectedVmId) {
      return { disabled: true, text: 'Select a VM', showSpinner: false };
    }
    return { disabled: false, text: error ? 'Retry Attach' : 'Attach Volume', showSpinner: false };
  };
  const buttonState = getButtonState();

const renderLeftPanelContent = () => {
    // Loading state
    if (isLoadingVms) {
      return (
        <div style={styles.loadingContainer}>
          <div style={styles.spinnerLight} />
          <p style={styles.loadingText}>Loading virtual machines...</p>
        </div>
      );
    }

// No VMs at all
    if (!validationResults.hasVms) {
      return (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <FaServer size={44} color="#555" />
          </div>
          <p style={styles.emptyTitle}>No Virtual Machines Found</p>
          <p style={styles.emptyText}>
            You haven't created any virtual machines in {sanitizeText(volumeRegion)} yet.<br />
            Create a VM in this region to attach this volume.
          </p>
        </div>
      );
    }
   // No VMs in region
    if (!validationResults.hasSameRegionVms) {
      return (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <FaServer size={44} color="#555" />
          </div>
          <p style={styles.emptyTitle}>No Virtual Machines Found</p>
          <p style={styles.emptyText}>
            You haven't created any virtual machines in {sanitizeText(volumeRegion)} yet.<br />
            Create a VM in this region to attach this volume.
          </p>
        </div>
      );
    }

    // VMs exist in region - show all with status (removed separate "No SHUTOFF VMs" section)

   

 // Normal state - VMs available
    return (
      <>
        {error && (
          <div style={styles.errorBox}>
            <FaExclamationTriangle size={16} color="#ef4444" style={{ marginTop: '2px', flexShrink: 0 }} />
            <span style={styles.errorText}>{sanitizeText(error)}</span>
          </div>
        )}
        
        <label style={styles.label}>Select Virtual Machine</label>
        <div style={styles.searchWrapper}>
          <FaSearch size={14} style={styles.searchIcon} />
     <input
  type="text"
  placeholder="Search VMs..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  maxLength={30}
  style={styles.input}
  disabled={isSubmitting}
/>
        </div>

        {filteredVms.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <FaSearch size={36} color="#555" />
            </div>
            <p style={styles.emptyTitle}>No Results Found</p>
            <p style={styles.emptyText}>
              No VMs matching "{sanitizeText(searchTerm)}"<br />
              Try a different search term.
            </p>
          </div>
        ) : (
          <div style={styles.vmList} className="volume-modal-scrollbar">
            {filteredVms.map(vm => {
              const vmId = getVMId(vm);
              const vmName = getVMName(vm);
              const vmStatus = getVMStatus(vm, volume?.region);
              const isSelectable = vmStatus === 'SHUTOFF';
              const isSelected = selectedVmId === vmId && isSelectable;
              const statusStyle = getStatusStyle(vmStatus);
              
              return (
                <div
                  key={vmId}
                  style={styles.vmCard(isSelected, !isSelectable)}
                  onClick={() => isSelectable && handleVmSelection(vmId)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={styles.radio(isSelected)}>
                        {isSelected && <div style={styles.radioInner} />}
                      </div>
                      <div>
                        <p style={styles.vmName}>{sanitizeText(vmName)}</p>
                        <p style={styles.vmRegion}>{sanitizeText(volumeRegion)}</p>
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: statusStyle.background,
                      color: statusStyle.color,
                      border: statusStyle.border,
                    }}>{vmStatus}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .volume-modal-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .volume-modal-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2a;
          border-radius: 3px;
        }
        .volume-modal-scrollbar::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        .volume-modal-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={styles.modal}>
          {/* LEFT PANEL */}
          <div style={styles.leftPanel}>
       <div style={styles.header}>
              <div style={styles.iconWrapper}>
                <FaLink size={20} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} />
              </div>
              <div>
                <h2 style={styles.title}>Attach Volume</h2>
                <p style={styles.subtitle}>Select a VM in SHUTOFF state</p>
              </div>
            </div>

            {renderLeftPanelContent()}
          </div>

          {/* RIGHT PANEL */}
          <div style={styles.rightPanel}>
            <h3 style={styles.summaryHeader}>VOLUME</h3>
            
            <p style={styles.summaryLabel}>Name</p>
            <p style={styles.summaryValue}>{sanitizeText(volumeName)}</p>
            
            <p style={styles.summaryLabel}>Size</p>
            <p style={styles.summaryValue}>{volumeSize} GB</p>
            
            <p style={styles.summaryLabel}>Type</p>
            <p style={styles.summaryValue}>{sanitizeText(volumeType)}</p>
            
            <p style={styles.summaryLabel}>Region</p>
            <p style={{ ...styles.summaryValue, color: '#e0eacc' }}>{sanitizeText(volumeRegion)}</p>

            <button
              onClick={handleSubmit}
              disabled={buttonState.disabled}
              style={styles.submitBtn(buttonState.disabled)}
            >
              {buttonState.showSpinner && <div style={styles.spinner} />}
              {buttonState.text}
            </button>
            
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={styles.cancelBtn(isSubmitting)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VolumeAttachModal;